import { useState, useMemo } from 'react';
import type { Transaction } from '../hooks/useAccountingData';

interface Props {
  data: Transaction[];
  loading: boolean;
}

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

function formatDate(s: string | null): string {
  if (!s) return '-';
  return s.slice(0, 10);
}

const PAYMENT_STATUS_MAP: Record<string, { label: string; color: string }> = {
  // 键名取 `PaymentStatus` 枚举原值（未付款/部分付款/已结清）。
  // 这里曾写「已付款」：改名后它永不命中，已结清的行会掉进下面那档灰底兜底，
  // 列表文字没错但绿标悄悄变成灰标——状态词的展示也必须跟枚举同源。
  '已结清': { label: '已结清', color: 'bg-emerald-50 text-emerald-600' },
  '部分付款': { label: '部分付款', color: 'bg-amber-50 text-amber-600' },
  '未付款': { label: '未付款', color: 'bg-rose-50 text-rose-500' },
};

const PAGE_SIZE = 15;

type FilterType = 'all' | 'income' | 'expense' | 'unpaid';

const isExpenseTx = (t: Transaction) => (t.orderCategory ?? '').includes('支出');

const scrollToNetting = () => {
  const el = document.getElementById('order-netting-section');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export default function TransactionList({ data, loading }: Props) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return data.filter(t => {
      if (filter === 'income' && isExpenseTx(t)) return false;
      if (filter === 'expense' && !isExpenseTx(t)) return false;
      if (filter === 'unpaid' && t.unpaidAmount <= 0) return false;
      if (search) {
        const kw = search.toLowerCase();
        return (
          t.orderNumber?.toLowerCase().includes(kw) ||
          t.customerName?.toLowerCase().includes(kw) ||
          t.serviceTypeName?.toLowerCase().includes(kw)
        );
      }
      return true;
    });
  }, [data, filter, search]);

  const totalPages = useMemo(() => {
    return Math.ceil(filtered.length / PAGE_SIZE);
  }, [filtered.length]);

  const pageData = useMemo(() => {
    return filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filtered, page]);

  const tabs = useMemo(() => {
    return [
      { key: 'all' as FilterType, label: '全部', count: data.length },
      { key: 'income' as FilterType, label: '收入', count: data.filter(t => !isExpenseTx(t)).length },
      { key: 'expense' as FilterType, label: '支出', count: data.filter(t => isExpenseTx(t)).length },
      { key: 'unpaid' as FilterType, label: '待收款', count: data.filter(t => t.unpaidAmount > 0).length },
    ];
  }, [data]);

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">收支明细</h3>
            <p className="text-xs text-gray-400 mt-0.5">所有订单的收支流水记录</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filter tabs */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 flex-wrap gap-0.5">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setFilter(tab.key); setPage(1); }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    filter === tab.key
                      ? 'bg-white text-gray-800'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  <span className={`text-xs px-1 rounded-full ${
                    filter === tab.key
                      ? 'bg-teal-100 text-teal-600'
                      : 'bg-gray-200 text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
              {/* 订单抵消功能 — 迁移到核算管理下方区域 */}
              <button
                type="button"
                onClick={scrollToNetting}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
              >
                <i className="ri-exchange-dollar-line text-xs" />
                抵消记录
              </button>
            </div>
            {/* Search */}
            <div className="relative">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-xs" />
              </div>
              <input
                type="text"
                placeholder="搜索客户/订单号"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 w-36"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/70">
              <th className="text-left text-xs font-medium text-gray-500 px-5 py-3">订单号</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">客户</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden md:table-cell">服务类型</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden lg:table-cell">活动日期</th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden sm:table-cell">支付方式</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">金额</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3 hidden sm:table-cell">已收</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">状态</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-t border-gray-50 animate-pulse">
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-gray-100 rounded" />
                    </td>
                  ))}
                </tr>
              ))
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-sm text-gray-400 py-12">暂无数据</td>
              </tr>
            ) : (
              pageData.map(t => {
                const isExpense = isExpenseTx(t);
                const ps = PAYMENT_STATUS_MAP[t.paymentStatus || ''] || { label: t.paymentStatus || '-', color: 'bg-gray-50 text-gray-500' };
                return (
                  <tr key={t.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isExpense ? 'bg-rose-400' : 'bg-emerald-500'}`} />
                        <span className="text-xs text-gray-600 font-mono">{t.orderNumber || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-gray-800">{t.customerName || '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <span className="text-xs text-gray-500">{t.serviceTypeName || '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <span className="text-xs text-gray-500">{formatDate(t.orderStartDate)}</span>
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{t.paymentMethod || '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className={`text-sm font-semibold ${isExpense ? 'text-rose-500' : 'text-gray-900'}`}>
                        {isExpense ? '-' : ''}{formatAmount(t.orderAmount)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                      {isExpense ? (
                        <span className="text-xs text-gray-400">-</span>
                      ) : (
                        <span className="text-sm text-emerald-600">{t.paidAmount > 0 ? formatAmount(t.paidAmount) : '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {isExpense ? (
                        <span className="text-xs px-2 py-0.5 bg-rose-50 text-rose-500 rounded-md">支出</span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-md ${ps.color}`}>{ps.label}</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-50">
          <span className="text-xs text-gray-400">{filtered.length} 条记录</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-sm" />
            </button>
            <span className="text-xs text-gray-600">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-sm" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
